import { useEffect, useRef } from 'react';
import { engine } from '../../core/audio/AudioEngine';

interface Props {
  channelId?: string;
  busId?: string;
  master?: boolean;
}

/** Vertical peak-hold meter fed by a channel, bus, or master AnalyserNode. */
export function Meter({ channelId, busId, master = false }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  const peakRef = useRef(0);

  useEffect(() => {
    let raf = 0;
    let buf: Uint8Array<ArrayBuffer> | null = null;

    const draw = () => {
      const canvas = ref.current;
      const g = canvas?.getContext('2d');
      if (canvas && g) {
        const analyser = master
        ? engine.masterAnalyser
        : busId
          ? engine.busAnalyser(busId)
          : engine.channelAnalyser(channelId ?? '');
        let peak = 0;
        if (analyser) {
          if (!buf || buf.length !== analyser.fftSize) buf = new Uint8Array(analyser.fftSize);
          analyser.getByteTimeDomainData(buf);
          for (let i = 0; i < buf.length; i++) {
            const v = Math.abs(buf[i] - 128) / 128;
            if (v > peak) peak = v;
          }
        }
        peakRef.current = Math.max(peak, peakRef.current * 0.86);
        const p = Math.min(1, peakRef.current);

        const W = canvas.width;
        const H = canvas.height;
        g.clearRect(0, 0, W, H);
        g.fillStyle = '#0a0d12';
        g.fillRect(0, 0, W, H);

        const greenTop = H * 0.7;
        const amberTop = H * 0.9;
        const peakY = H - p * H;

        if (peakY < H) {
          g.fillStyle = '#3ddc84';
          g.fillRect(0, Math.max(peakY, greenTop), W, H - Math.max(peakY, greenTop));
          if (peakY < amberTop) {
            g.fillStyle = '#ffb454';
            g.fillRect(0, Math.max(peakY, amberTop), W, greenTop - Math.max(peakY, amberTop));
          }
          if (peakY < greenTop) {
            g.fillStyle = '#ff4d5a';
            g.fillRect(0, peakY, W, greenTop - peakY);
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [channelId, busId, master]);

  return <canvas ref={ref} className="meter" width={12} height={150} />;
}